import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  getStoredAuthorities,
  isLoggedIn,
} from 'src/consumer/services/patientProspectService'

// Surfaces the "you have a care request in progress" reminder for users who
// hold both ROLE_USER (wellness) and ROLE_PATIENT_PROSPECT, since the
// RootLanding redirect only fires for prospect-ONLY users (per
// docs/PATIENT_PROSPECT_SIGNUP_PLAN.md §9). Without this banner, multi-role
// users land on the wellness home and lose the trail back to /care/status.
//
// Hidden on /care/* paths (where the user is already in the prospect
// experience) and dismissible per browser session via sessionStorage.

const DISMISS_KEY = 'careRequestBannerDismissed'

function isMultiRoleProspect(authorities) {
  if (!Array.isArray(authorities)) return false
  if (!authorities.includes('ROLE_PATIENT_PROSPECT')) return false
  // Only show when the user *also* has another active persona — otherwise
  // RootLanding has already redirected them to /care/status.
  const otherPersonas = ['ROLE_USER', 'ROLE_PATIENT', 'ROLE_PROFESSIONAL_USER']
  return authorities.some((r) => otherPersonas.includes(r))
}

export default function CareRequestInProgressBanner() {
  const location = useLocation()
  const [dismissed, setDismissed] = useState(false)

  const visible = useMemo(() => {
    if (!isLoggedIn()) return false
    if (location.pathname.startsWith('/care/')) return false
    if (location.pathname.startsWith('/patient-home')) return false
    return isMultiRoleProspect(getStoredAuthorities())
  }, [location.pathname])

  useEffect(() => {
    try {
      setDismissed(window.sessionStorage.getItem(DISMISS_KEY) === '1')
    } catch {
      // ignore
    }
  }, [])

  if (!visible || dismissed) return null

  const handleDismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // ignore
    }
    setDismissed(true)
  }

  return (
    <div
      role="status"
      style={{
        margin: '8px auto',
        maxWidth: 760,
        padding: '10px 14px',
        borderRadius: 12,
        background: '#F4F1FF',
        border: '1px solid #E0D9FB',
        color: '#3F2E78',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 14,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      <span aria-hidden style={{ fontSize: 18, lineHeight: 1 }}>🩺</span>
      <div style={{ flex: 1, lineHeight: 1.4 }}>
        <strong>You have a care request in progress.</strong>{' '}
        <Link
          to="/care/status"
          style={{ color: '#5B45BF', fontWeight: 600, textDecoration: 'underline' }}
        >
          View status
        </Link>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          border: 0,
          color: '#5B45BF',
          fontSize: 18,
          lineHeight: 1,
          cursor: 'pointer',
          padding: '4px 6px',
        }}
      >
        ×
      </button>
    </div>
  )
}
